import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../../lib/ep';
import { useCSS } from '../../../lib/hooks';

import ActionButton, { SecondaryButton } from '../../builder/ActionButton';
import ButtonCollection from '../../builder/ButtonCollection';
import FieldLabel from '../../builder/FieldLabel';
import FloatingLayer from '../../builder/FloatingLayer';
import SaveButton from '../../builder/SaveButton';
import SectionSubtitle from '../../builder/SectionSubtitle';
import SectionTitle from '../../builder/SectionTitle';
import TextArea from '../../builder/TextArea';
import Input from '../../builder/Input';
import Spinner from '../../builder/Spinner';
import ProgressBar from '../../builder/ProgressBar';

interface AITranslateType {
	field:string,
	locale:string,
	is_image?:boolean,
	original:string,
	promotion_id?:number,
	change:(field:string, value:string) => void,
};

interface AIMultiTranslateType {
	originals:{ key:string, value:string, label:string }[],
	locale:string,
	promotion_id?:number,
	exclusions:string,
	/**If disabled the translations will be done automatically displaying a working screen and added to the translations */
	disable_preview?:boolean,
	change:(field:string, value:string) => void,
};

export const AIMultiTranslate = ({ originals, locale, promotion_id, change, exclusions, disable_preview, ...props }:AIMultiTranslateType) => {
	useCSS('comp/ai_translations.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_overlord = props?.is_overlord ?? false;

	const [ preview, setPreview ] = useState(false);
	const [ working, setWorking ] = useState(false);
	const [ progress, setProgress ] = useState({ percent:0, total_chunks:1 });
	const [ done, setDone ] = useState(false);
	const [ translations, setTranslations ] = useState(false);

	const getOriginals = () => {
		if(Array.isArray(originals)) return originals;
		return originals();
	};

	useEffect(() => {
		if(disable_preview == undefined || disable_preview === false) return;
		if(!preview) return;
		if(getOriginals().length == 0) return;
		if(is_overlord === false) return;

		setWorking(true);
		automatic_translations();
	}, [ preview ]);

	const automatic_translations = async () => {
		const translations = await ai_propose();
		for(let key in translations) {
			change(key, translations[key]);
		}

		setDone(true);
		setPreview(false);
	};

	const split_translations = () => {
		const split_words = 100;
		const originals =  getOriginals();
		const chunks = [{}];
		let current_length = 0;
		let current_chunk = 0;
		originals.map((original) => {
			const length = original.value.split(" ").length;
			if(current_length + length > split_words) {
				current_chunk++;
				current_length = 0;
				chunks.push({});
			}
			chunks[current_chunk][original.key] = original.value;
			current_length += length;
		});

		return chunks;
	}


	const ai_propose = async (cb) => {
		setWorking(true);

		const chunks = split_translations();

		setProgress({ percent:0, total_chunks:chunks.length });

		let translated = {};
		for(var i in chunks) {
			const data = { multiple:JSON.stringify(chunks[i]), locale };
	
			if(exclusions != undefined && exclusions != "") data.exclusions = exclusions;
	
			const response = await fetch(`/v24/i18n_assistant/${promotion_id}`, {
				method:'POST',
				headers:{
					'Accept':'application/json',
					'Content-Type':'application/json',
				},
				body:JSON.stringify(data),
			}).then((r) => r.json());

			translated = { ...translated, ...response };
			setProgress({ percent:((parseInt(i) + 1) / chunks.length) * 100, total_chunks:chunks.length });
		}

		if(disable_preview != undefined && disable_preview) {
			if(cb != undefined) cb(translated);
			return translated;
		}

		if(chunks.length > 1) {
			setProgress({ percent:100, total_chunks:chunks.length });

			setTimeout(() => {
				setTranslations(translated);
				setDone(true);
				setWorking(false);
			}, 1000);

			return;
		} else {
			setTranslations(translated);
			setDone(true);
			setWorking(false);
		}
	};

	const openLayerWithAIProposals = () => {
		setPreview(true);
		if(is_overlord) ai_propose();
	};

	const accept = (_translations) => {
		const tr = (_translations != undefined) ? _translations : translations;

		for(let key in tr) {
			change(key, tr[key]);
		}

		setPreview(false);
	};

	const close = () => {
		setWorking(false);
		setPreview(false);
	};
	
	const has_pending_originals = Array.isArray(originals) ? ((originals.length > 0) ? true : false):true;

	const getLabelLength = (l:String) => {
		const length = l.length;
		//if(length > 400) return 'huge';
		if(length > 200) return 'normal';
		if(length > 120) return 'small';
		return 'input';
	};

	const onProgressChange = () => {
		if(disable_preview == undefined || disable_preview === false) {
			openLayerWithAIProposals();
		} else {
			if(props.onProgressChange != undefined) props.onProgressChange(true);

			ai_propose((responses) => {
				accept(responses);
				if(props.onProgressChange != undefined) props.onProgressChange(false);
			});
		}
	};

	return (
		<>
			<ActionButton
				icon='ep-ai'
				disabled={!has_pending_originals}
				onClick={onProgressChange}
			>Translate with AI</ActionButton>

			{(preview != false) && (
				<FloatingLayer backButton={close}>
					<div>
						<SectionTitle>AI-powered automatic translation</SectionTitle>

						{(is_overlord === false && done === false && working === false) && (
							<>
								<SectionSubtitle>This tool allows you to automatically translate all pending texts using AI.</SectionSubtitle>

								<SectionSubtitle
									autotranslate={false}
								><strong>{i18n("Before applying them, you'll be able to review and edit the translations.")}</strong></SectionSubtitle>

								<SectionSubtitle
									autotranslate={false}
								>{`${i18n("Note")}: ${i18n("Terms and conditions, privacy policy and cookies policy are excluded from the AI translation.")}`}</SectionSubtitle>
							</>
						)}

						{(is_overlord || (done === false && working)) && (
							<>
								<SectionSubtitle>Looking for and translating all pending texts...</SectionSubtitle>

								<SectionSubtitle>This process may take a few seconds.</SectionSubtitle>

								{(progress.total_chunks > 1) && (
									<ProgressBar
										actual={progress.percent}
										max={100}
										display_percent_in_bar={false}
										animation_duration='0.05s'
									>Translating</ProgressBar>
								)}

								{(progress.total_chunks == 1) && (
									<div
										style={{
											height:'4em',
											position:'relative',
										}}
									>
										<Spinner />
									</div>
								)}
							</>
						)}

						{(done && working === false && !has_pending_originals) && (
							<p>All texts are already translated</p>
						)}

						{(done && working === false && has_pending_originals) && (
							<div className="multi_translation in_layer">
								{working === false && getOriginals().map((translation) => {
									const label_length = getLabelLength(translation.value);
									const is_long_label = (!['input'].includes(label_length)) ? true : false;
									const InputComponent = (is_long_label) ? TextArea : Input;

									const container_classes = ['single_label', `${(is_long_label) ? 'is_long' : 'is_short'}`];

									return (
										<div key={translation.key} className={container_classes.join(" ")}>
											<FieldLabel>{translation.label}</FieldLabel>

											<div class="t">
												<div className="o">{translation.value}</div>

												<span className="sep ep-next" />

												<InputComponent
													name={translation.key}
													value={(translations != false) ? translations[translation.key] : ""}
													onChange={(e) => setTranslations({ ...translations, [e.target.name]:e.target.value })}
													_class={{ first:true }}
												/>
											</div>
										</div>
									)
								})}
							</div>
						)}

						<ButtonCollection
							in_floating={true}
						>
							{(is_overlord === false && done === false && working === false) && (
								<ActionButton
									onClick={() => ai_propose()}
								>Yes, continue</ActionButton>
							)}

							{(is_overlord === false && (done || working)) && (
								<ActionButton
									onClick={() => accept()}
									disabled={(done === false) ? true : false}
								>Apply</ActionButton>
							)}

							<SecondaryButton
								onClick={close}
							>Cancelar</SecondaryButton>
						</ButtonCollection> 
					</div>
				</FloatingLayer>
			)}
		</>
	)
};

const AITranslate = ({ field, locale, is_image, original, promotion_id, change, ...props }:AITranslateType) => {
	useCSS('comp/ai_translations.css');

	const ai_propose = async (e, cb) => {
		const response = await fetch(`/v24/i18n_assistant/${promotion_id}`, {
			method:'POST',
			headers:{
				'Accept':'application/json',
				'Content-Type':'application/json',
			},
			body:JSON.stringify({ copy:original, locale }),
		}).then((r) => r.json());

		change(field, response.translation);
		if(cb != undefined) cb();
	};

	if(is_image) return null;

	return (
		<ButtonCollection
			_class={{ other:'ai_btn gui_flex_end' }}
		>
			<SaveButton
				squared={true}
				icon='ep-ai mainButton'
				onClick={ai_propose}
				color='white'
				disabled={props.disabled}
			/>
		</ButtonCollection>
	)
};

export default injectIntl(AITranslate);